import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, btnPrimary, btnSecondary, card, EmptyState, InlineError, Modal, SkeletonRows, useToast } from '../ui/Kit';
import { fmtDate } from './common';

// Candidate database → Duplicates (bundle 4a-2). Likely = a side-by-side merge suggestion; possible = a badge to check;
// definite = an attach whose details differed (pick which value to keep). Nothing is merged without a person.

const REASON = {
  same_phone_and_name: 'Same phone + same name', same_name_and_company: 'Same name + same company',
  same_name_city_title: 'Same name + city + similar title', same_name_location: 'Same name + same location',
  fuzzy_name_location: 'Similar name + same location', matched_same_incoming: 'One record matched both',
  same_email: 'Same e-mail', same_linkedin: 'Same LinkedIn', same_file: 'Same resume file', near_identical_text: 'Near-identical resume',
};
const FIELD = { full_name: 'Name', city: 'City', state: 'State', country: 'Country', current_title: 'Current title',
  current_company: 'Current company', total_years: 'Years', relevant_years: 'Relevant years', work_authorization: 'Work authorization', visa_type: 'Visa' };
const TIER = { likely: ['Likely', 'bg-[#FEF3C7] text-[#92400E]'], possible: ['Possible', 'bg-[#F1F5F9] text-[#475569]'],
  definite: ['Details differ', 'bg-[#E8F1FB] text-[#003D82]'] };

export function MergeDialog({ id, onClose, onDone }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [d, setD] = useState(null);
  const [keep, setKeep] = useState(null);
  const [take, setTake] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api(`/api/candidates/merge-suggestions/${id}`).then((x) => { setD(x); setKeep(x.candidate.id); }).catch((e) => setError(e.message));
  }, [id]);
  const run = async (path, body, msg) => {
    setBusy(true); setError('');
    try { const r = await api(path, { method: 'POST', body }); toast.show(msg); onDone(r); } catch (e) { setError(e.message); }
    setBusy(false);
  };
  if (!d) return <Modal open title="Possible duplicate" onClose={onClose}>{error ? <InlineError>{error}</InlineError> : <p className="text-sm text-[#64748B]">Loading…</p>}</Modal>;
  const s = d.suggestion;
  if (!d.other) {                                       // a definite attach whose details differed
    const conflicts = Object.entries(s.conflicts || {});
    return (
      <Modal open title={`Details differ — ${d.candidate.full_name}`} onClose={onClose} width="max-w-xl"
        footer={<><button className={btnSecondary} onClick={() => run(`/api/candidates/merge-suggestions/${id}/resolve`, { use_incoming: [] }, 'Kept the current details')}>Keep all current</button>
          <button className={btnPrimary} disabled={busy} data-testid="resolve" onClick={() => run(`/api/candidates/merge-suggestions/${id}/resolve`,
            { use_incoming: Object.keys(take).filter((k) => take[k]) }, 'Details updated')}>Save choices</button></>}>
        <p className="text-sm text-[#64748B] mb-3">A new resume or record matched this candidate ({(s.reasons || []).map((r) => REASON[r] || r).join(', ')}) but some details differ. Choose what to keep.</p>
        <table className="w-full text-sm"><thead className="text-[11px] uppercase text-[#64748B]"><tr><th className="text-left py-1">Field</th><th className="text-left">Current</th><th className="text-left">New</th></tr></thead>
          <tbody className="divide-y divide-[#EEF1F6]">{conflicts.map(([f, v]) => (
            <tr key={f}><td className="py-2 font-semibold">{FIELD[f] || f}</td>
              <td><label className="inline-flex gap-2 items-center"><input type="radio" name={f} checked={!take[f]} onChange={() => setTake({ ...take, [f]: false })} />{String(v.current)}</label></td>
              <td><label className="inline-flex gap-2 items-center"><input type="radio" name={f} checked={!!take[f]} onChange={() => setTake({ ...take, [f]: true })} />{String(v.incoming)}</label></td></tr>))}</tbody></table>
        <div className="mt-3"><InlineError>{error}</InlineError></div>
      </Modal>
    );
  }
  const a = d.candidate;
  const b = d.other;
  const other = keep === a.id ? b : a;
  const kept = keep === a.id ? a : b;
  const col = (c) => (
    <label className={`block border rounded-xl p-3 cursor-pointer ${keep === c.id ? 'border-[#0066CC] bg-[#F5F9FF]' : 'border-[#E6E9F0]'}`}>
      <span className="flex items-center gap-2"><input type="radio" name="keep" checked={keep === c.id} onChange={() => { setKeep(c.id); setTake({}); }} />
        <b>{c.full_name}</b><span className="text-xs text-[#64748B]">{c.candidate_code}</span></span>
      <span className="block text-xs text-[#64748B] mt-1">{[c.current_title, c.current_company].filter(Boolean).join(' · ')}</span>
      <span className="block text-xs text-[#64748B]">{[c.email, c.phone].filter(Boolean).join(' · ') || 'No contact'} · added {fmtDate(c.created_at)}</span>
      <button type="button" className="text-xs text-[#0066CC] font-semibold mt-1 hover:underline" onClick={(e) => { e.preventDefault(); navigate(`/candidates/${c.id}`); }}>Open profile</button>
    </label>
  );
  return (
    <Modal open title="Possible duplicate — compare" onClose={onClose} width="max-w-3xl"
      footer={<><button className={btnSecondary} disabled={busy} onClick={() => run(`/api/candidates/merge-suggestions/${id}/dismiss`, undefined, 'Marked as different people')} data-testid="dismiss">Not the same person</button>
        <button className={btnPrimary} disabled={busy} data-testid="merge" onClick={() => run(`/api/candidates/merge-suggestions/${id}/merge`,
          { keep_id: keep, take_from_other: Object.keys(take).filter((k) => take[k]) }, 'Merged')}>{busy ? 'Merging…' : `Merge into ${kept.full_name}`}</button></>}>
      <p className="text-sm text-[#64748B] mb-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold mr-2 ${TIER[s.tier][1]}`}>{TIER[s.tier][0]}</span>
        {(s.reasons || []).map((r) => REASON[r] || r).join(' · ')}</p>
      <div className="grid sm:grid-cols-2 gap-3">{col(a)}{col(b)}</div>
      <h4 className="text-sm font-bold mt-4 mb-1">Details — the kept record’s value unless you pick the other</h4>
      <table className="w-full text-sm" data-testid="merge-fields"><tbody className="divide-y divide-[#EEF1F6]">
        {d.fields.filter((f) => f.candidate != null || f.other != null).map((f) => {
          const mine = keep === a.id ? f.candidate : f.other;
          const theirs = keep === a.id ? f.other : f.candidate;
          return (
            <tr key={f.field} className={f.differs ? 'bg-[#FFFBEB]' : ''}>
              <td className="py-2 pr-3 font-semibold w-40">{FIELD[f.field] || f.field}</td>
              <td className="pr-3"><label className="inline-flex gap-2 items-center"><input type="radio" name={`f-${f.field}`} checked={!take[f.field]} onChange={() => setTake({ ...take, [f.field]: false })} />{mine == null ? '—' : String(mine)}</label></td>
              <td><label className="inline-flex gap-2 items-center"><input type="radio" name={`f-${f.field}`} disabled={theirs == null} checked={!!take[f.field]} onChange={() => setTake({ ...take, [f.field]: true })} />{theirs == null ? '—' : String(theirs)}</label></td>
            </tr>
          );
        })}
        <tr><td className="py-2 pr-3 font-semibold">Parsed profile</td>
          <td colSpan={2}><label className="inline-flex gap-2 items-center"><input type="checkbox" checked={!!take.profile} onChange={(e) => setTake({ ...take, profile: e.target.checked })} />Use {other.full_name}’s experience and skills</label></td></tr>
      </tbody></table>
      <p className="text-xs text-[#64748B] mt-3">Contacts, documents, applications and activity of both are kept. Applications to the same job become one.</p>
      <div className="mt-3"><InlineError>{error}</InlineError></div>
    </Modal>
  );
}

export default function DuplicatesPanel() {
  const [tier, setTier] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    setError(null);
    return api(`/api/candidates/merge-suggestions?status=open${tier ? `&tier=${tier}` : ''}`).then(setData).catch((e) => setError(e));
  }, [tier]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3">
      <div className="flex gap-1 p-1 bg-[#EEF1F6] rounded-xl w-max" role="group" aria-label="Tier">
        {[['', 'All'], ['likely', 'Likely'], ['possible', 'Possible'], ['definite', 'Details differ']].map(([k, l]) => (
          <button key={k || 'all'} aria-pressed={tier === k} onClick={() => setTier(k)} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${tier === k ? 'bg-white shadow-sm text-[#0F172A]' : 'text-[#475569]'}`}>{l}</button>
        ))}
      </div>
      {error ? <div className={card + ' p-5'}><InlineError>{error.message}{error.reference ? ` (reference ${error.reference})` : ''}</InlineError></div>
        : !data ? <div className={card}><SkeletonRows rows={4} cols={3} /></div>
          : !data.rows.length ? <div className={card + ' p-2'}><EmptyState title="No duplicates to review" hint="Possible duplicates found during imports show here." /></div> : (
            <ul className={card + ' divide-y divide-[#EEF1F6]'} data-testid="dup-list">
              {data.rows.map((r) => (
                <li key={r.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-2">
                  <span className={`inline-flex w-max px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${TIER[r.tier][1]}`}>{TIER[r.tier][0]}</span>
                  <span className="flex-1 text-sm"><b>{r.candidate_name}</b>{r.other_candidate_id ? <> and <b>{r.other_name}</b></> : ''}
                    <span className="block text-xs text-[#64748B]">{(r.reasons || []).map((x) => REASON[x] || x).join(' · ')} · {fmtDate(r.created_at)}</span></span>
                  <button className={btnSecondary} onClick={() => setOpen(r.id)}>{r.other_candidate_id ? 'Compare' : 'Review'}</button>
                </li>
              ))}
            </ul>
          )}
      {open && <MergeDialog id={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); load(); }} />}
    </div>
  );
}
